import Logger, { LoggingModule } from 'services/Logger';
import * as Config from 'modules/utils/ConfigUtils';
import * as HttpStatus from 'http-status-codes';
import { Store } from 'redux';
import { RootState } from 'store/types';
import axios, { AxiosError } from 'axios';
import { oc } from 'ts-optchain';
import {
  dfdRefreshTokenSelector,
  isDfdAuthenticatedSelector
} from 'store/authentication/selectors';
import interceptorRefreshTokenManager from './refreshInterceptorAlertManager';
import { dfdRefreshTokenManager } from './RefreshTokenManager';

let isRefreshing = false;

const errorInterceptor = async (store: Store<RootState>, error: AxiosError) => {
  const { config, response } = error;
  const state = store.getState();
  const status = oc(response).status();
  const hasDfdRefreshToken = dfdRefreshTokenSelector(state);
  const isAuthExpired = !isDfdAuthenticatedSelector(state);

  if (Config.UNAUTHORIZED_HTTP_CODE.includes(status)) {
    Logger.debug(
      `${LoggingModule.responseInterceptors}Axios Response Interceptor UNAUTHORIZED Error`,
      error
    );

    let dfdAccessToken;

    if (!isRefreshing) {
      isRefreshing = true;

      try {
        if (hasDfdRefreshToken && isAuthExpired) {
          const dfdAuth = await dfdRefreshTokenManager.refresh(store);
          dfdAccessToken = oc(dfdAuth).dfdAuth.accessToken();

          if (dfdAccessToken) {
            config.headers = {
              ...config.headers,
              Authorization: `Bearer ${dfdAccessToken}`
            };
          } else {
            interceptorRefreshTokenManager.logoutAndAlert(store);
            return Promise.reject(error);
          }
        }
      } catch (err) {
        Logger.error(
          `${LoggingModule.responseInterceptors}Failed to refresh token in response interceptor`,
          error
        );
      } finally {
        isRefreshing = false;
      }
    }
    // put new tokens back in and resubmit request
    if (config && dfdAccessToken) {
      // Infinite loop won't happen on retry, because this is a different axios client which doesn't have response interceptor
      const retryRequest = new Promise(resolve => {
        resolve(axios(config));
      });
      return retryRequest;
    }
  }

  if (error.code === 'ECONNABORTED' || status === HttpStatus.REQUEST_TIMEOUT) {
    Logger.error(`${LoggingModule.responseInterceptors}FAIL - request timed out`, error);
  }

  return Promise.reject(error);
};

const responseInterceptors = [
  {
    error: errorInterceptor
  }
];

export default responseInterceptors;
